import KeyboardJS from 'keyboardjs';

export interface Focusable
{
    focus: boolean;
    focusVisible: boolean;
    activate(): void;
    skip?: boolean;
    length?: number;
}

export type FocusContext = Focusable[];

export class FocusManager
{
    private static _instance: FocusManager = null;
    public static get instance() { return this._instance; }

    private contexts: FocusContext[] = [];
    private categoryLen: number;
    private current: Focusable = null;
    public inCategory: boolean;

    constructor()
    {
        FocusManager._instance = this;

        // because we add this when the game starts up, without any keyboard context, these
        // handlers get called in any and all contexts
        KeyboardJS.bind('tab', this.next.bind(this), () => {});
        KeyboardJS.bind('shift + tab', this.prev.bind(this), () => {});
        KeyboardJS.bind('enter', null, this.activate.bind(this));
        window.addEventListener('mousemove', this.clearFocus.bind(this));
    }

    private next(e: KeyboardJS.KeyEvent)
    {
        e.preventDefault();
        const context = this.contexts[this.contexts.length - 1];

        if (!context)
        {
            return;
        }
        const prev = this.current;
        do
        {
            // if current is null, index will be -1, and immediately increased to 0
            let index = context.indexOf(this.current);
            if (++index >= context.length)
            {
                index = 0;
            }
            
            this.current = context[index];
                   
        } while (!this.current || this.current.skip)
        // clear previous focus
        if (prev)
        {
            prev.focus = false;
        }
        // select new focus
        this.current.focus = true;
    }

    private prev(e: KeyboardJS.KeyEvent)
    {
        e.preventDefault();
        const context = this.contexts[this.contexts.length - 1];
        if (!context)
        {
            return;
        }
        const prev = this.current;
        do
        {
            // if current is null, index will be -1, and immediately decreased to -2, and then
            // set to the last index in the array
            let index = context.indexOf(this.current);
            if (--index < 0)
            {
                index = context.length - 1;
            }
            // select new focus
            this.current = context[index];
        } while (!this.current || this.current.skip)
        // clear previous focus
        if (prev)
        {
            prev.focus = false;
        }
        // select new focus
        this.current.focus = true;
    }

    private activate()
    {
        if (this.current)
        {
            this.current.activate();
        }
        this.inCategory = false;
    }

    public get focused() { return this.current; }
    public set focused(target: Focusable)
    {
        if (target !== this.current)
        {
            if (this.current)
            {
                this.current.focus = false;
            }
            this.current = target;
            if (target)
            {
                target.focus = true;
            }
        }
    }

    public clearFocus()
    {
        this.focused = null;
    }

    /**
     * Overrides the current context with a new context (and clears focus)
     */
    public activateContext(context: FocusContext): void
    {
        this.clearFocus();
        this.contexts.push(context);
    }

    /**
     * Removes the most recently added context (and clears focus)
     */
    public popContext(): void
    {
        this.clearFocus();
        this.contexts.pop();
    }
}
